// TeamsPage.jsx
import { useState, useEffect, useMemo } from "react";
import {
  Plus,
  Send,
  RefreshCcw,
  ChevronRight,
  ChevronDown,
  Users,
  Store,
  UserPlus,
  Building2,
  Phone,
  Mail,
  BadgeCheck,
  MapPin,
  Search,
  Save,
  X,
  Loader2,
  Bell,
} from "lucide-react";
import { Toaster, toast } from "sonner";
import LoadingScreen from "../Utils/LoadingScreen";
import { useGlobalContext } from "../../GlobalContext";

// Reuse your existing modals
import SendNotificationModal from "../Driver/SendNotificationModal";
import BulkSendNotificationModal from "../Driver/BulkSendNotificationModal";
import TablePagination from "../Utils/TablePagination";
import useTeams from "../../Sevices/useTeams";
import useShop from "../../Sevices/useShop";
import useDriverAllApi from "../../Sevices/Driver Api/useDriverAllApi";

export default function TeamsPage() {
  const {
     loading: teamActionLoading,
    createTeam,
    updateDutyStatus,
    assignShopToTeam,
  } = useTeams();

  const { teams, fetchDriverTeams } = useDriverAllApi();

const { baseurl, token, accessToken, AdminId, setIsUnauthorised } = useGlobalContext();

const { shops, fetchShops, setShops } = useShop({
  baseurl,
  accessToken,
  token,
  AdminId,
  setIsUnauthorised,
});

  const [currentPage, setCurrentPage] = useState(1);
  const [search, setSearch] = useState("");
  const [teamStatusFilter, setTeamStatusFilter] = useState("");

  // Shops (assign one-by-one)
  const [assignOpen, setAssignOpen] = useState(false);
  const [assignTeam, setAssignTeam] = useState(null);
  const [assignSearch, setAssignSearch] = useState("");
  const [assignWorkingCode, setAssignWorkingCode] = useState(""); // show spinner per-row

  // Notifications
  const [singleNotifOpen, setSingleNotifOpen] = useState(false);
  const [singleNotifDriver, setSingleNotifDriver] = useState(null);
  const [bulkNotifOpen, setBulkNotifOpen] = useState(false);
  const [bulkDrivers, setBulkDrivers] = useState([]);

  // Duty toggle (local state cache)
  const [dutyWorkingId, setDutyWorkingId] = useState(null);
  const [dutyMap, setDutyMap] = useState(new Map()); // driverId -> "on_duty"/"off_duty"

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [teamName, setTeamName] = useState("");
  const [accuracy, setAccuracy] = useState("");
  const [expanded, setExpanded] = useState(new Set());

  const teamsPerPage = 15;

  const filteredTeams = useMemo(() => {
  if (!Array.isArray(teams)) return [];

  return teams.filter(
    (team) =>
      (!teamStatusFilter || team.status === teamStatusFilter) &&
      (search === "" ||
        team.team_name?.toLowerCase().includes(search.toLowerCase()) ||
        team.createdOn?.toLowerCase().includes(search.toLowerCase()))
  );
}, [teams, teamStatusFilter, search]);

  const indexOfLast = currentPage * teamsPerPage;
  const indexOfFirst = indexOfLast - teamsPerPage;
  const currentTeams = filteredTeams.slice(indexOfFirst, indexOfLast);
  const totalPages = Math.ceil(filteredTeams.length / teamsPerPage);

  useEffect(() => {
     fetchDriverTeams()
    fetchShops();
  }, []);

  const handleCreateTeam = async () => {
    await createTeam(teamName, accuracy, () => {
      setShowCreateModal(false);
      setTeamName("");
      setAccuracy("");
      fetchDriverTeams();
    });
  };

  const toggleExpand = (id) => {
    setExpanded((prev) => {
      const copy = new Set(prev);
      copy.has(id) ? copy.delete(id) : copy.add(id);
      return copy;
    });
  };

  // ---------- Notifications ----------
  const openSingleNotification = (driver, team) => {
    // Map team driver shape -> modal expected shape
    const mapped = {
      driverId: driver.driver_code || driver.id, // use driver_code as ID
      fullName:
        driver.fullName ||
        `${driver.first_name || ""} ${driver.last_name || ""}`.trim(),
      username: driver.username || "",
      status: driver.status || "",
      teamName: team?.team_name || "",
    };
    setSingleNotifDriver(mapped);
    setSingleNotifOpen(true);
  };

  const openBulkNotification = (team) => {
    // Map all team drivers -> Bulk modal shape
    const mapped = (team?.driverlist || []).map((d) => ({
      driverId: d.driver_code || d.id,
      fullName:
        d.fullName || `${d.first_name || ""} ${d.last_name || ""}`.trim(),
      username: d.username || "",
      status: d.status || "",
      teamName: team.team_name,
    }));
    setBulkDrivers(mapped);
    setBulkNotifOpen(true);
  };

  // ---------- Duty toggle (from DriversPage logic) ----------
  const handleToggleDutyStatus = async (driverCode) => {
    const current = dutyMap.get(driverCode) || "off_duty";
    const next = current === "on_duty" ? "off_duty" : "on_duty";

    setDutyWorkingId(driverCode);
    setDutyMap((prev) => new Map(prev).set(driverCode, next));

    await updateDutyStatus(driverCode, next, () =>
      setDutyMap((prev) => new Map(prev).set(driverCode, current)),
    );

    setDutyWorkingId(null);
  };

  // ---------- Assign Shops (single per call) ----------
  const openAssignShops = async (team) => {
    setAssignTeam(team);
    if (shops.length === 0) await fetchShops();
    setAssignSearch("");
    setAssignOpen(true);
  };

  const assignOneShop = async (shop) => {
    if (!assignTeam) return;
    const code = shop.shop_code;

    const curTeamName = (shop.teamName || "").trim();
    const isElsewhere =
      curTeamName &&
      curTeamName.toLowerCase() !== (assignTeam.team_name || "").toLowerCase();

    if (isElsewhere) {
      toast.error(`Already assigned to ${curTeamName}`);
      return;
    }

    const alreadyInThisTeam = (assignTeam.shopList || []).some(
      (s) => s.shop_code === code,
    );
    if (alreadyInThisTeam) {
      toast.info("This shop is already mapped to the same team.");
      return;
    }

    setAssignWorkingCode(code);

    await assignShopToTeam(shop, assignTeam, async () => {
      await fetchDriverTeams();

      // keep UI in sync without closing modal
      setAssignTeam((prev) =>
        prev
          ? {
              ...prev,
              shopList: [
                ...(prev.shopList || []),
                {
                  id: shop.id,
                  shop_code: shop.shop_code,
                  shop_name: shop.shop_name,
                  shop_address: shop.shop_address,
                  shop_phone: shop.shop_phone,
                  onboardedOn: shop.onboardedOn,
                  areaid: shop.areaid,
                },
              ],
            }
          : prev,
      );
    });

    setAssignWorkingCode("");
  };

  const filteredShopsForAssign = useMemo(() => {
    const q = assignSearch.trim().toLowerCase();
    const list = shops || [];
    if (!q) return list;
    return list.filter(
      (s) =>
        s.shop_name?.toLowerCase().includes(q) ||
        s.shop_code?.toLowerCase().includes(q) ||
        s.shop_address?.toLowerCase().includes(q),
    );
  }, [shops, assignSearch]);

  const StatusPill = ({ status }) => {
    const s = (status || "").toLowerCase();
    const classes =
      s === "publish"
        ? "bg-green-100 text-green-700"
        : s === "pending"
          ? "bg-yellow-100 text-yellow-700"
          : "bg-gray-100 text-gray-600";
    const label = s ? s.charAt(0).toUpperCase() + s.slice(1) : "—";
    return (
      <span
        className={`px-2 py-1 rounded-full text-xs font-semibold ${classes}`}
      >
        {label}
      </span>
    );
  };

  useEffect(() => {
    const total = Math.max(1, Math.ceil(filteredTeams.length / teamsPerPage));

    if (currentPage > total) {
      setCurrentPage(total);
    }
    if (currentPage < 1) {
      setCurrentPage(1);
    }
  }, [filteredTeams.length, teamsPerPage, currentPage]);

  return (
    <div className="p-6">
      <Toaster position="top-right" />
      <div className="flex justify-between items-center mb-4">
        <h1 className="text-xl font-semibold">Teams</h1>
      </div>

      <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
        {/*  Search & Filters (left) */}
        <div className="flex flex-wrap items-end gap-2">
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search team name or date..."
            className="px-2 py-1 border rounded text-xs shadow-sm w-[180px]"
          />
          <select
            value={teamStatusFilter}
            onChange={(e) => setTeamStatusFilter(e.target.value)}
            className="text-xs border rounded px-2 py-1 shadow-sm w-[130px]"
          >
            <option value="">All Status</option>
            <option value="publish">Publish</option>
            <option value="pending">Pending</option>
          </select>
        </div>

        {/*  Actions (right) */}
        <div className="flex flex-wrap items-end gap-2">
          <button
            className="flex items-center space-x-1 bg-blue-600 hover:bg-blue-700 text-white px-3 py-1.5 rounded text-xs shadow"
            onClick={() => setShowCreateModal(true)}
          >
            <Plus size={14} />
            <span>Create Team</span>
          </button>
          <button
            className="flex items-center space-x-1 bg-gray-500 hover:bg-gray-600 text-white px-3 py-1.5 rounded text-xs shadow"
          onClick={() => fetchDriverTeams()}

          >
            <RefreshCcw size={14} />
            <span>Refresh</span>
          </button>
        </div>
      </div>

      {teams.length === 0 ? (
  <LoadingScreen />
) : (


        <>
          <div className="overflow-x-auto border rounded-md shadow-sm">
            <table className="min-w-full bg-white">
              <thead>
                <tr className="bg-orange-400 text-left text-sm font-semibold text-white">
                  <th className="px-3 py-2 w-10"></th>
                  <th className="px-4 py-2 text-center">ID</th>
                  <th className="px-4 py-2 text-center">Team Name</th>
                  <th className="px-4 py-2 text-center">Created On</th>
                  <th className="px-4 py-2 text-center">Accuracy</th>
                  <th className="px-4 py-2 text-center">Status</th>
                  <th className="px-4 py-2 text-center">Members</th>
                  <th className="px-4 py-2 text-center">Shops</th>
                  <th className="px-4 py-2 text-center">Actions</th>
                </tr>
              </thead>

              <tbody>
                {currentTeams.map((team) => {
                  const isOpen = expanded.has(team.id);
                  const members = Array.isArray(team.driverlist)
                    ? team.driverlist
                    : [];
                  const shopsMapped = Array.isArray(team.shopList)
                    ? team.shopList
                    : [];

                  return (
                    <FragmentRow
                      key={team.id}
                      team={team}
                      isOpen={isOpen}
                      members={members}
                      shops={shopsMapped}
                      dutyMap={dutyMap}
                      dutyWorkingId={dutyWorkingId}
                      onToggleDuty={handleToggleDutyStatus}
                      onToggle={() => toggleExpand(team.id)}
                      onOpenAssign={() => openAssignShops(team)}
                      onOpenSingleNotif={(driver) =>
                        openSingleNotification(driver, team)
                      }
                      onOpenBulkNotif={() => openBulkNotification(team)}
                    />
                  );
                })}
              </tbody>
            </table>
          </div>

    
          {/* Pagination */}
          <TablePagination
            currentPage={currentPage}
            totalCount={filteredTeams.length}
            pageSize={teamsPerPage}
            onPageChange={setCurrentPage}
          />
        </>
      )}

      {/* Create Team Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white w-full max-w-md rounded-lg shadow-lg p-6 relative">
            <h2 className="text-lg font-semibold mb-4">Create New Team</h2>

            <div className="mb-3">
              <label className="block text-sm font-medium mb-1">
                Team Name
              </label>
              <input
                type="text"
                value={teamName}
                onChange={(e) => setTeamName(e.target.value)}
                className="w-full border px-3 py-2 rounded text-sm"
                placeholder="Enter team name"
              />
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium mb-1">Accuracy</label>
              <select
                value={accuracy}
                onChange={(e) => setAccuracy(e.target.value)}
                className="w-full border px-3 py-2 rounded text-sm"
              >
                <option value="">Select accuracy</option>
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
              </select>
            </div>

            <div className="flex justify-end gap-2">
              <button
                onClick={() => setShowCreateModal(false)}
                className="px-3 py-1.5 bg-gray-300 rounded hover:bg-gray-400 text-sm"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateTeam}
                className="px-3 py-1.5 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm"
              >
                Create
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Assign Shops Modal (single assign per click) */}
      {assignOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white w-full max-w-4xl rounded-lg shadow-lg overflow-hidden">
            <div className="px-5 py-3 border-b flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold">Assign Shops</h3>
                <p className="text-xs text-gray-500">
                  Team:{" "}
                  <span className="font-medium">{assignTeam?.team_name}</span>
                </p>
              </div>
              <button
                onClick={() => setAssignOpen(false)}
                className="p-2 rounded hover:bg-gray-100"
                aria-label="Close"
              >
                <X size={18} />
              </button>
            </div>

            <div className="p-5">
              <div className="mb-3">
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
                    <Search size={16} />
                  </span>
                  <input
                    value={assignSearch}
                    onChange={(e) => setAssignSearch(e.target.value)}
                    placeholder="Search shop by name, code, or address..."
                    className="w-full border rounded-lg pl-9 pr-3 py-2 text-sm"
                  />
                </div>
              </div>

              <div className="max-h-[60vh] overflow-y-auto border rounded-lg">
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-100 text-gray-700">
                    <tr>
                      <th className="px-3 py-2 text-left">Shop</th>
                      <th className="px-3 py-2 text-left">Code</th>
                      <th className="px-3 py-2 text-left">Address</th>
                      <th className="px-3 py-2 text-left">Phone</th>
                      <th className="px-3 py-2 text-left">Team (current)</th>
                      <th className="px-3 py-2 text-left w-40">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredShopsForAssign.map((s) => {
                      const assignedTo = s.teamName?.trim() || "";
                      const assignedElsewhere =
                        assignedTo &&
                        assignedTo.toLowerCase() !==
                          (assignTeam?.team_name || "").toLowerCase();
                      const alreadyInThisTeam = (
                        assignTeam?.shopList || []
                      ).some((x) => x.shop_code === s.shop_code);
                      const disabled = assignedElsewhere || alreadyInThisTeam;
                      const disabledReason = assignedElsewhere
                        ? `Assigned to: ${assignedTo}`
                        : alreadyInThisTeam
                          ? "Already in this team"
                          : "";

                      return (
                        <tr key={s.id} className="border-t">
                          <td className="px-3 py-2 font-medium">
                            {s.shop_name}
                          </td>
                          <td className="px-3 py-2">{s.shop_code}</td>
                          <td className="px-3 py-2">
                            <div className="truncate max-w-[360px]">
                              {s.shop_address}
                            </div>
                          </td>
                          <td className="px-3 py-2">{s.shop_phone}</td>
                          <td className="px-3 py-2">
                            {assignedTo || "—"}
                            {assignedElsewhere && (
                              <span className="ml-2 text-[11px] bg-gray-100 text-gray-700 px-2 py-0.5 rounded-full">
                                Assigned to: {assignedTo}
                              </span>
                            )}
                          </td>
                          <td className="px-3 py-2">
                            <button
                              className={`inline-flex items-center gap-2 px-3 py-1.5 rounded text-white text-xs ${
                                disabled
                                  ? "bg-gray-300 cursor-not-allowed"
                                  : "bg-emerald-600 hover:bg-emerald-700"
                              }`}
                              disabled={
                                disabled || assignWorkingCode === s.shop_code
                              }
                              onClick={() => assignOneShop(s)}
                              title={disabledReason}
                            >
                              {assignWorkingCode === s.shop_code ? (
                                <>
                                  <Loader2 size={14} className="animate-spin" />
                                  Assigning...
                                </>
                              ) : (
                                <>
                                  <Building2 size={14} />
                                  Assign
                                </>
                              )}
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                    {filteredShopsForAssign.length === 0 && (
                      <tr>
                        <td
                          colSpan={6}
                          className="text-center text-gray-500 py-8"
                        >
                          No shops found.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              <div className="mt-4 flex justify-end">
                <button
                  onClick={() => setAssignOpen(false)}
                  className="px-4 py-2 rounded bg-gray-200 hover:bg-gray-300 text-sm"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Notification Modals */}
      <SendNotificationModal
        isOpen={singleNotifOpen}
        onClose={() => setSingleNotifOpen(false)}
        driver={singleNotifDriver}
        baseurl={baseurl}
        token={token}
        setIsUnauthorised={setIsUnauthorised}
      />

      <BulkSendNotificationModal
        isOpen={bulkNotifOpen}
        onClose={() => setBulkNotifOpen(false)}
        drivers={bulkDrivers}
        baseurl={baseurl}
        token={token}
        setIsUnauthorised={setIsUnauthorised}
        defaultTeam={""}
        defaultStatus={""}
      />
    </div>
  );
}

/* ---------- Helpers / subcomponents ---------- */
function FragmentRow({
  team,
  isOpen,
  members,
  shops,
  dutyMap,
  dutyWorkingId,
  onToggleDuty,
  onToggle,
  onOpenAssign,
  onOpenSingleNotif,
  onOpenBulkNotif,
}) {
  return (
    <>
      {/* Collapsed Row — unchanged */}
      <tr className="text-sm border-t">
        <td className="px-3 py-2 text-center align-middle">
          <button
            onClick={onToggle}
            className="p-1 rounded hover:bg-gray-100 transition"
            aria-label={isOpen ? "Collapse" : "Expand"}
          >
            {isOpen ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
          </button>
        </td>
        <td className="px-4 py-2 text-center">{team.id}</td>
        <td className="px-4 py-2 font-medium text-center">{team.team_name}</td>
        <td className="px-4 py-2 text-center">{team.createdOn}</td>
        <td className="px-4 py-2 text-center">{team.location_accuracy}</td>
        <td className="px-4 py-2 text-center">
          <StatusPillInline status={team.status} />
        </td>
        <td className="px-4 py-2 text-center">
          <span className="inline-flex items-center gap-1 text-xs bg-blue-50 text-blue-700 px-2 py-0.5 rounded-full">
            <Users size={14} /> {members.length}
          </span>
        </td>
        <td className="px-4 py-2 text-center">
          <span className="inline-flex items-center gap-1 text-xs bg-emerald-50 text-emerald-700 px-2 py-0.5 rounded-full">
            <Store size={14} /> {shops.length}
          </span>
        </td>
        <td className="px-4 py-2 text-center text-blue-600 hover:underline cursor-pointer">
          Edit
        </td>
      </tr>

      {/* Expanded Content */}
      {isOpen && (
        <tr className="bg-gray-50">
          <td colSpan={9} className="px-4 py-3">
            <div className="grid md:grid-cols-2 gap-4">
              {/* Members */}
              <section className="border rounded-lg bg-white">
                <div className="flex items-center justify-between px-3 py-2 border-b">
                  <div className="flex items-center gap-2">
                    <Users size={16} />
                    <h3 className="text-sm font-semibold">Team Members</h3>
                  </div>
                  <div className="flex items-center gap-2">
                    <button className="inline-flex items-center gap-1 text-xs bg-blue-600 hover:bg-blue-700 text-white px-2.5 py-1 rounded">
                      <UserPlus size={14} />
                      Add Member
                    </button>
                    {/* Bulk notification next to Add Member */}
                    <button
                      onClick={onOpenBulkNotif}
                      className="inline-flex items-center gap-1 text-xs bg-purple-600 hover:bg-purple-700 text-white px-2.5 py-1 rounded"
                      title="Send a notification to all members of this team"
                    >
                      <Send size={14} />
                      Send Bulk
                    </button>
                  </div>
                </div>

                <div className="p-3">
                  {members.length === 0 ? (
                    <p className="text-xs text-gray-500">No members added.</p>
                  ) : (
                    <ul className="space-y-2">
                      {members.map((m) => {
                        const driverId = m.driver_code || m.id;
                        const fullName =
                          m.fullName ||
                          `${m.first_name || ""} ${m.last_name || ""}`.trim();
                        const duty = dutyMap.get(driverId) || "off_duty";
                        const isOn = duty === "on_duty";
                        const working = dutyWorkingId === driverId;

                        return (
                          <li
                            key={driverId}
                            className="border rounded-md px-3 py-2 text-xs bg-white hover:bg-gray-50"
                          >
                            <div className="flex flex-wrap justify-between gap-2">
                              <div className="font-medium">
                                {fullName || "—"}
                              </div>
                              <span className="inline-flex items-center gap-1">
                                <BadgeCheck size={14} className="opacity-70" />
                                {m.status || "—"}
                              </span>
                            </div>

                            <div className="mt-1 grid grid-cols-1 md:grid-cols-4 gap-2 text-gray-600">
                              <div className="flex items-center gap-1">
                                <BadgeCheck size={14} className="opacity-60" />
                                <span className="truncate">
                                  {m.driver_code}
                                </span>
                              </div>
                              <div className="flex items-center gap-1">
                                <Phone size={14} className="opacity-60" />
                                <span className="truncate">
                                  {m.phone || "—"}
                                </span>
                              </div>
                              <div className="hidden md:flex items-center gap-1">
                                <Mail size={14} className="opacity-60" />
                                <span className="truncate">
                                  {m.email || "—"}
                                </span>
                              </div>

                              {/* Actions: duty toggle + single notification */}
                              <div className="flex items-center justify-end gap-3">
                                {/* Duty toggle */}
                                <label
                                  className={`relative inline-flex items-center ${
                                    working
                                      ? "opacity-60 pointer-events-none"
                                      : "cursor-pointer"
                                  }`}
                                  title="Toggle duty status"
                                >
                                  <input
                                    type="checkbox"
                                    className="sr-only peer"
                                    checked={isOn}
                                    onChange={() =>
                                      !working && onToggleDuty(driverId)
                                    }
                                    disabled={working}
                                    aria-checked={isOn}
                                    aria-label="Toggle duty status"
                                  />
                                  <div
                                    className={[
                                      "w-10 h-5 rounded-full transition-colors duration-300",
                                      "bg-gray-300 peer-checked:bg-green-500",
                                      "ring-0 focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-green-400",
                                    ].join(" ")}
                                  />
                                  <span
                                    className={[
                                      "absolute left-0.5 top-0.5 h-4 w-4 rounded-full bg-white shadow",
                                      "transition-transform duration-300",
                                      "peer-checked:translate-x-[1.25rem]",
                                    ].join(" ")}
                                  />
                                </label>

                                {/* Single notification */}
                                <button
                                  onClick={() => onOpenSingleNotif(m)}
                                  className="text-blue-600 hover:text-blue-800"
                                  title="Send notification"
                                >
                                  <Bell size={16} />
                                </button>
                              </div>
                            </div>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>
              </section>

              {/* Shops */}
              <section className="border rounded-lg bg-white">
                <div className="flex items-center justify-between px-3 py-2 border-b">
                  <div className="flex items-center gap-2">
                    <Store size={16} />
                    <h3 className="text-sm font-semibold">Assigned Shops</h3>
                  </div>
                  <button
                    onClick={onOpenAssign}
                    className="inline-flex items-center gap-1 text-xs bg-emerald-600 hover:bg-emerald-700 text-white px-2.5 py-1 rounded"
                  >
                    <Building2 size={14} />
                    Assign Shops
                  </button>
                </div>

                <div className="p-3">
                  {shops.length === 0 ? (
                    <p className="text-xs text-gray-500">No shops assigned.</p>
                  ) : (
                    <ul className="space-y-2">
                      {shops.map((s) => (
                        <li
                          key={s.id}
                          className="border rounded-md px-3 py-2 text-xs bg-white hover:bg-gray-50"
                        >
                          <div className="flex flex-wrap justify-between gap-2">
                            <div className="font-medium">{s.shop_name}</div>
                            <span className="text-[11px] bg-gray-100 text-gray-700 px-2 py-0.5 rounded-full">
                              {s.shop_code}
                            </span>
                          </div>
                          <div className="mt-1 grid grid-cols-1 md:grid-cols-3 gap-2 text-gray-600">
                            <div className="flex items-center gap-1 md:col-span-2">
                              <MapPin size={14} className="opacity-60" />
                              <span className="truncate">{s.shop_address}</span>
                            </div>
                            <div className="flex items-center gap-1">
                              <Phone size={14} className="opacity-60" />
                              <span>{s.shop_phone || "—"}</span>
                            </div>
                          </div>
                          <div className="mt-1 text-[11px] text-gray-500">
                            Onboarded: {s.onboardedOn || "—"}
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </section>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

function StatusPillInline({ status }) {
  const s = (status || "").toLowerCase();
  const classes =
    s === "publish"
      ? "bg-green-100 text-green-700"
      : s === "pending"
        ? "bg-yellow-100 text-yellow-700"
        : "bg-gray-100 text-gray-600";
  const label = s ? s.charAt(0).toUpperCase() + s.slice(1) : "—";
  return (
    <span className={`px-2 py-1 rounded-full text-xs font-semibold ${classes}`}>
      {label}
    </span>
  );
}
